/**
 * BI > Linhas de saída — modal de filtros, prévia JSON e CSV.
 * Base: doc/queries/bi-output-lines.sql (serviço `output_lines_report`).
 */
import api from '../api.js';
import { notify } from '../../versa/msgbox.js';
import dialog from '../../versa/dialog.js';
import { formatBiHttpError } from './bi-http-error.js';

function $(sel, root = document) {
	return root.querySelector(sel);
}

function defaultDateFirstOfMonth() {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function defaultToday() {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

async function loadCompanies(selectEl) {
	const res = await api.list_all_companies();
	if (!res.ok) {
		notify(await res.text(), 'Empresas');
		return;
	}
	let list;
	try {
		list = await res.json();
	} catch (e) {
		notify('Resposta de empresas inválida.', 'Empresas');
		return;
	}
	if (!Array.isArray(list)) return;
	selectEl.innerHTML = '<option value="">— Selecione —</option>';
	for (const c of list) {
		const o = document.createElement('option');
		o.value = c.id;
		o.textContent = c.nick || c.name || c.id;
		selectEl.appendChild(o);
	}
}

function collectParams(node, format) {
	const companyId = $('#biol-company', node)?.value?.trim();
	if (!companyId) {
		notify('Selecione a empresa.');
		return null;
	}
	const df = $('#biol-date-from', node)?.value?.trim();
	if (!df) {
		notify('Informe a data inicial.');
		return null;
	}
	const sp = new URLSearchParams();
	sp.set('format', format);
	sp.set('date_from', df);
	const dt = $('#biol-date-to', node)?.value?.trim();
	if (dt) sp.set('date_to', dt);
	if ($('#biol-include-disabled', node)?.checked) sp.set('include_disabled', '1');
	const sid = $('#biol-output-id', node)?.value?.trim();
	if (sid) sp.set('output_id', sid);
	const oid = $('#biol-order-id', node)?.value?.trim();
	if (oid) sp.set('output_order_id', oid);
	const pid = $('#biol-product-id', node)?.value?.trim();
	if (pid) sp.set('product_id', pid);
	else {
		const ps = $('#biol-product-search', node)?.value?.trim();
		if (ps) sp.set('product_search', ps);
	}
	if (format === 'json') {
		sp.set('limit', $('#biol-limit', node)?.value || '200');
		sp.set('offset', $('#biol-offset', node)?.value || '0');
	} else {
		const mx = $('#biol-max-rows', node)?.value?.trim();
		if (mx) sp.set('max_rows', mx);
	}
	return { companyId, sp };
}

function escCell(s) {
	if (s == null) return '';
	const t = document.createElement('template');
	t.textContent = String(s);
	return t.innerHTML;
}

function renderPreviewTable(container, rows) {
	if (!rows?.length) {
		container.innerHTML = '<p class="muted mts">Nenhuma linha (ajuste filtros ou permissões).</p>';
		return;
	}
	const keys = Object.keys(rows[0]);
	let html = '<div class="biol-scroll mts"><table class="view fsx"><thead><tr>';
	for (const k of keys) {
		html += `<th>${escCell(k)}</th>`;
	}
	html += '</tr></thead><tbody>';
	for (const r of rows) {
		html += '<tr>';
		for (const k of keys) {
			html += `<td>${escCell(r[k])}</td>`;
		}
		html += '</tr>';
	}
	html += '</tbody></table></div>';
	container.innerHTML = html;
}

function downloadCsv(blob, filename) {
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'bi-linhas-saida.csv';
	a.click();
	URL.revokeObjectURL(a.href);
}

export default async function bi_output_lines() {
	const work = document.getElementById('workarea');
	work.innerHTML = `
		<style>
			.biol-hint { color: #555; font-size: 0.9rem; max-width: 48rem; }
			.biol-scroll { overflow: auto; max-height: 65vh; }
			.biol-modal label { display: block; margin-top: 0.65rem; }
			.biol-modal .row { display: flex; flex-wrap: wrap; gap: 0.75rem 1rem; align-items: flex-end; margin-top: 0.35rem; }
			.biol-modal .row > label { flex: 1 1 12rem; }
			.biol-actions { margin-top: 1rem; display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
			.biol-check { margin-top: 0.75rem; display: flex; align-items: center; gap: 0.5rem; }
		</style>
		<div class="title">Linhas de saída (BI)</div>
		<p class="mts biol-hint">Extração por <strong>período de criação da saída</strong> (fuso América/São Paulo), com empresa, opcionalmente saída, pedido de saída e produto. Inclui status da saída e do pedido, cliente, lote, quantidades, <strong>armazém</strong> e <strong>posição (endereço)</strong> consumida em cada linha do item. Use <strong>Prévia</strong> ou <strong>CSV</strong>.</p>
		<p class="mts"><button type="button" class="button" id="biol-open-modal">Filtros e geração</button></p>
		<div id="biol-preview-host" class="mts"></div>
	`;

	const previewHost = $('#biol-preview-host', work);

	const openModal = () => {
		const dlgHtml = `
<div class="msgbox biol-modal" style="min-width: min(38rem, 92vw); max-width: 96vw; text-align: left;">
	<div class="mb-title">Linhas de saída — filtros</div>
	<div class="mb-text" style="max-height: none;">
		<label>Empresa<sup>*</sup><select id="biol-company"></select></label>
		<div class="row">
			<label>Data inicial<sup>*</sup><input type="date" id="biol-date-from" required></label>
			<label>Data final<input type="date" id="biol-date-to" title="Vazio = até hoje (fuso SP)"></label>
		</div>
		<div class="biol-check">
			<input type="checkbox" id="biol-include-disabled">
			<label for="biol-include-disabled" style="margin:0;">Incluir saídas excluídas (status 999)</label>
		</div>
		<div class="row">
			<label>Nº saída (opcional)<input type="number" id="biol-output-id" min="1" step="1" placeholder="id outputs"></label>
			<label>Nº pedido saída (opcional)<input type="number" id="biol-order-id" min="1" step="1" placeholder="id output_orders"></label>
		</div>
		<div class="row">
			<label>ID produto (opcional)<input type="number" id="biol-product-id" min="1" step="1" placeholder="sobrepor busca texto"></label>
			<label>Busca produto (SKU ou nome)<input type="text" id="biol-product-search" placeholder="se não informou ID" autocomplete="off"></label>
		</div>
		<div class="row">
			<label>Prévia: limite<input type="number" id="biol-limit" value="200" min="1" max="5000" step="1"></label>
			<label>Prévia: offset<input type="number" id="biol-offset" value="0" min="0" step="1"></label>
			<label>CSV: máx. linhas<input type="number" id="biol-max-rows" value="50000" min="1" max="200000" step="1"></label>
		</div>
	</div>
	<div class="biol-actions">
		<button type="button" class="button bn_Preview">Prévia na tela</button>
		<button type="button" class="button bn_ExportCsv">Exportar CSV</button>
		<button type="button" class="button bn_Escape">Fechar</button>
	</div>
</div>`;

		const dlg = dialog.create(dlgHtml, async (key) => {
			const root = dlg.getNode();
			if (key === 'Escape') {
				dlg.destroy();
				return;
			}
			if (key !== 'Preview' && key !== 'ExportCsv') return;

			const pack = collectParams(root, key === 'Preview' ? 'json' : 'csv');
			if (!pack) return;

			try {
				if (key === 'Preview') {
					const res = await api.output_lines_report(pack.companyId, pack.sp.toString());
					const raw = await res.text();
					if (!res.ok) {
						notify(formatBiHttpError(res.status, raw), 'Linhas de saída');
						return;
					}
					let data;
					try {
						data = JSON.parse(raw);
					} catch (e) {
						notify('Resposta não é JSON.', 'Linhas de saída');
						return;
					}
					if (!Array.isArray(data)) {
						notify('Formato inesperado da API.', 'Linhas de saída');
						return;
					}
					dlg.destroy();
					renderPreviewTable(previewHost, data);
					notify(`Prévia: ${data.length} linha(s).`, 'Linhas de saída');
					return;
				}
				const res = await api.output_lines_report(pack.companyId, pack.sp.toString());
				if (!res.ok) {
					const t = await res.text();
					notify(formatBiHttpError(res.status, t), 'Linhas de saída');
					return;
				}
				const blob = await res.blob();
				const cd = res.headers.get('Content-Disposition');
				let fname = 'bi-linhas-saida.csv';
				if (cd) {
					const m = cd.match(/filename="?([^";]+)"?/i);
					if (m) fname = m[1];
				}
				downloadCsv(blob, fname);
				dlg.destroy();
				notify('Download do CSV iniciado.', 'Linhas de saída');
			} catch (e) {
				console.error(e);
				notify(e?.message || String(e), 'Linhas de saída');
			}
		});

		const root = dlg.getNode();
		const df = $('#biol-date-from', root);
		const dt = $('#biol-date-to', root);
		if (df && !df.value) df.value = defaultDateFirstOfMonth();
		if (dt && !dt.value) dt.value = defaultToday();
		loadCompanies($('#biol-company', root)).then(() => {});
	};

	$('#biol-open-modal', work).addEventListener('click', openModal);
}
